import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { LotteryBallGroup } from '../components/LotteryBall';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { toast } from 'sonner';
import { 
  History as HistoryIcon, 
  Calendar,
  Shield,
  Target,
  Zap,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const strategyIcons = {
  conservative: Shield,
  balanced: Target,
  risky: Zap
};

const strategyNames = {
  conservative: 'Conservadora',
  balanced: 'Balanceada',
  risky: 'Arriesgada'
};

const strategyColors = {
  conservative: 'text-green-400',
  balanced: 'text-blue-400',
  risky: 'text-pink-400'
};

const History = () => {
  const { token } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [lotteries, setLotteries] = useState([]);
  const [selectedLottery, setSelectedLottery] = useState('all');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [recsRes, lotteriesRes] = await Promise.all([
        axios.get(`${API}/recommendations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/lotteries`)
      ]);
      
      setRecommendations(recsRes.data);
      setLotteries(lotteriesRes.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const filteredRecommendations = selectedLottery === 'all' 
    ? recommendations 
    : recommendations.filter(r => r.lottery_id === selectedLottery);

  const paginatedRecommendations = filteredRecommendations.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const totalPages = Math.ceil(filteredRecommendations.length / itemsPerPage);

  const getLotteryName = (lotteryId) => {
    const lottery = lotteries.find(l => l.id === lotteryId);
    return lottery?.name || 'Desconocido';
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      <Navbar />
      
      <main className="pt-20 px-4 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-3" data-testid="history-title">
              <HistoryIcon className="text-green-400" />
              Historial de Recomendaciones
            </h1>
            <p className="text-slate-400">
              {filteredRecommendations.length} recomendaciones generadas
            </p>
          </div>
          
          <Select value={selectedLottery} onValueChange={(v) => { setSelectedLottery(v); setPage(1); }}>
            <SelectTrigger className="w-[200px] bg-slate-900 border-slate-700 text-white" data-testid="history-lottery-filter">
              <SelectValue placeholder="Filtrar por lotería" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white hover:bg-slate-800 focus:bg-slate-800">
                Todas las loterías
              </SelectItem>
              {lotteries.map((lottery) => (
                <SelectItem 
                  key={lottery.id} 
                  value={lottery.id}
                  className="text-white hover:bg-slate-800 focus:bg-slate-800"
                >
                  {lottery.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Disclaimer */}
        <div className="mb-6">
          <DisclaimerBanner variant="inline" />
        </div>

        {/* Recommendations List */}
        {paginatedRecommendations.length > 0 ? (
          <div className="space-y-4">
            {paginatedRecommendations.map((rec, idx) => {
              const StrategyIcon = strategyIcons[rec.strategy];
              return (
                <Card 
                  key={rec.id || idx} 
                  className="glass-card hover:border-slate-600 transition-all"
                  data-testid={`history-item-${idx}`}
                >
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* Lottery & Strategy Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-white font-bold">{getLotteryName(rec.lottery_id)}</span>
                          <span className={`flex items-center gap-1 text-sm ${strategyColors[rec.strategy]}`}>
                            <StrategyIcon size={14} />
                            {strategyNames[rec.strategy]}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-400 text-sm">
                          <Calendar size={14} />
                          {formatDate(rec.created_at)}
                        </div>
                      </div>
                      
                      {/* Numbers */}
                      <div className="flex items-center gap-4">
                        <LotteryBallGroup numbers={rec.numbers} size="md" />
                        <div className="text-right">
                          <p className="text-slate-400 text-xs">Selección histórica</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="glass-card" data-testid="no-history">
            <CardContent className="py-12 text-center">
              <HistoryIcon size={48} className="mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400 text-lg mb-2">Sin recomendaciones aún</p>
              <p className="text-slate-500 text-sm">
                Ve a la sección de recomendaciones para generar tus primeros números
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="prev-page"
            >
              <ChevronLeft size={18} />
            </Button>
            
            <span className="text-slate-400">
              Página <span className="text-white font-mono">{page}</span> de{' '}
              <span className="text-white font-mono">{totalPages}</span>
            </span>
            
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="next-page"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        )}
      </main>

      <DisclaimerBanner variant="fixed" />
    </div>
  );
};

export default History;
